import ignore, { Ignore } from 'ignore';
import JSZip from 'jszip';
import {
  ALWAYS_IGNORED_UPLOAD_DIRS,
  ALWAYS_IGNORED_UPLOAD_FILES,
  isDefaultIgnoredUploadPath,
} from '@internal/plugin-cloudflare-workers-common';

/**
 * Shared client-side project-upload processing.
 *
 * Turns a folder or zip selection into the base64-encoded, DEFLATE-compressed
 * zip string the scaffolder templates expect for their `projectUpload`
 * parameter (consumed by the `make:extract-upload` action). This is the single
 * source of truth shared by the scaffolder `ProjectUpload` field and the
 * in-plugin "Add"/"Update" wizards so both filter and pack uploads identically.
 */

export const ALWAYS_IGNORED_DIRS = ALWAYS_IGNORED_UPLOAD_DIRS;
export const ALWAYS_IGNORED_FILES = ALWAYS_IGNORED_UPLOAD_FILES;

export interface UploadProcessOptions {
  /** Skip the built-in dotfile / dependency / lockfile filtering. */
  skipDefaultIgnores?: boolean;
  /** Skip honouring a `.gitignore` found at the upload root. */
  skipGitignore?: boolean;
}

export interface ProcessedUpload {
  /** base64-encoded DEFLATE zip ready to hand to the scaffolder. */
  base64: string;
  /** Number of (non-directory) files included after filtering. */
  fileCount: number;
}

export interface ProcessedUploadBlob {
  /** DEFLATE zip blob ready to POST to the dedicated uploads endpoint. */
  blob: Blob;
  /** Real byte size of the blob (no base64 inflation). */
  sizeBytes: number;
  /** Number of (non-directory) files included after filtering. */
  fileCount: number;
}

/** Decides whether a relative path should be excluded from the upload. */
export function shouldSkip(
  path: string,
  options: { skipDefaultIgnores: boolean; gitignore?: Ignore },
): boolean {
  // .git is always filtered regardless of settings.
  if (path === '.git' || path.startsWith('.git/') || path.includes('/.git/'))
    return true;
  if (!options.skipDefaultIgnores && isDefaultIgnoredUploadPath(path)) {
    return true;
  }
  if (options.gitignore && options.gitignore.ignores(path)) return true;
  return false;
}

/** Human-readable byte size, e.g. "1.5 MB". */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
  const i = Math.min(
    Math.floor(Math.log(bytes) / Math.log(k)),
    sizes.length - 1,
  );
  return `${(bytes / k ** i).toFixed(1)} ${sizes[i]}`;
}

/** Returns the relative path of a selected file (folder uploads carry one). */
function relativePathOf(file: File): string {
  return (
    (file as File & { webkitRelativePath?: string }).webkitRelativePath ||
    file.name
  );
}

/**
 * Builds a filtered `JSZip` from a folder selection (a `FileList` from a
 * `webkitdirectory` input, or an array of files), stripping the common
 * top-level folder prefix so the archive root matches the project root.
 * Shared by both the base64 and blob output variants below.
 */
async function buildZipFromFiles(
  files: FileList | File[],
  options: UploadProcessOptions,
): Promise<JSZip> {
  const { skipDefaultIgnores = false, skipGitignore = false } = options;
  const list = Array.from(files);
  const zip = new JSZip();

  // Find a common root prefix from the relative paths (folder uploads).
  const paths = list.map(relativePathOf);
  const parts = paths.map(p => p.split('/'));
  let commonPrefix = '';
  if (parts.length > 0 && parts[0].length > 1) {
    commonPrefix = `${parts[0][0]}/`;
    const allMatch = parts.every(p => p[0] === parts[0][0]);
    if (!allMatch) commonPrefix = '';
  }

  // Honour a root .gitignore unless asked not to.
  let gitignore: Ignore | undefined;
  if (!skipGitignore) {
    for (const file of list) {
      const rel = relativePathOf(file).replace(commonPrefix, '');
      if (rel === '.gitignore') {
        gitignore = ignore().add(await file.text());
        break;
      }
    }
  }

  const skipOpts = { skipDefaultIgnores, gitignore };
  for (const file of list) {
    const relativePath = relativePathOf(file).replace(commonPrefix, '');
    if (shouldSkip(relativePath, skipOpts)) continue;
    zip.file(relativePath, await file.arrayBuffer());
  }

  return zip;
}

function countZipFiles(zip: JSZip): number {
  return Object.keys(zip.files).filter(k => !zip.files[k].dir).length;
}

/**
 * Packs a folder selection into a filtered base64 zip ready to hand to the
 * scaffolder as an inline `make:extract-upload` payload (legacy path).
 */
export async function processFilesToZipBase64(
  files: FileList | File[],
  options: UploadProcessOptions = {},
): Promise<ProcessedUpload> {
  const zip = await buildZipFromFiles(files, options);
  const base64 = await zip.generateAsync({
    type: 'base64',
    compression: 'DEFLATE',
  });
  return { base64, fileCount: countZipFiles(zip) };
}

/**
 * Packs a folder selection into a filtered zip Blob, for POSTing to the
 * dedicated `/uploads` endpoint. Reports real byte size (no base64 tax).
 */
export async function processFilesToZipBlob(
  files: FileList | File[],
  options: UploadProcessOptions = {},
): Promise<ProcessedUploadBlob> {
  const zip = await buildZipFromFiles(files, options);
  const blob = await zip.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
  });
  return { blob, sizeBytes: blob.size, fileCount: countZipFiles(zip) };
}

/**
 * Builds a filtered `JSZip` from an uploaded `.zip` file, applying the same
 * ignore rules and common-prefix stripping as folder uploads. Shared by both
 * the base64 and blob output variants below.
 */
async function buildZipFromZipFile(
  file: File,
  options: UploadProcessOptions,
): Promise<JSZip> {
  const { skipDefaultIgnores = false, skipGitignore = false } = options;
  const original = await JSZip.loadAsync(await file.arrayBuffer());

  // Find a common root prefix among the zip entries.
  const allNames = Object.keys(original.files).filter(
    n => !original.files[n].dir,
  );
  let zipPrefix = '';
  if (allNames.length > 0) {
    const parts = allNames.map(n => n.split('/'));
    if (parts[0].length > 1) {
      const candidate = `${parts[0][0]}/`;
      if (parts.every(p => p[0] === parts[0][0])) zipPrefix = candidate;
    }
  }

  // Honour a root .gitignore unless asked not to.
  let gitignore: Ignore | undefined;
  if (!skipGitignore) {
    const gitignoreEntry = original.file(`${zipPrefix}.gitignore`);
    if (gitignoreEntry && !gitignoreEntry.dir) {
      gitignore = ignore().add(await gitignoreEntry.async('text'));
    }
  }

  const skipOpts = { skipDefaultIgnores, gitignore };
  const filtered = new JSZip();
  for (const [name, entry] of Object.entries(original.files)) {
    if (entry.dir) continue;
    const relative = zipPrefix ? name.replace(zipPrefix, '') : name;
    if (!relative) continue;
    if (shouldSkip(relative, skipOpts)) continue;
    filtered.file(relative, await entry.async('arraybuffer'));
  }

  return filtered;
}

/**
 * Re-packs an uploaded `.zip` file into a filtered base64 zip ready to hand
 * to the scaffolder as an inline `make:extract-upload` payload (legacy path).
 */
export async function processZipFileToBase64(
  file: File,
  options: UploadProcessOptions = {},
): Promise<ProcessedUpload> {
  const filtered = await buildZipFromZipFile(file, options);
  const base64 = await filtered.generateAsync({
    type: 'base64',
    compression: 'DEFLATE',
  });
  return { base64, fileCount: countZipFiles(filtered) };
}

/**
 * Re-packs an uploaded `.zip` file into a filtered zip Blob, for POSTing to
 * the dedicated `/uploads` endpoint. Reports real byte size (no base64 tax).
 */
export async function processZipFileToZipBlob(
  file: File,
  options: UploadProcessOptions = {},
): Promise<ProcessedUploadBlob> {
  const filtered = await buildZipFromZipFile(file, options);
  const blob = await filtered.generateAsync({
    type: 'blob',
    compression: 'DEFLATE',
  });
  return { blob, sizeBytes: blob.size, fileCount: countZipFiles(filtered) };
}
